/**
 * Comp-off requests, cancellation of leave and amendment of approved leave (ADR 0031 addendum,
 * Phase 4b).
 *
 * A SEPARATE ROUTER, SAME `/leave` PREFIX as `leave.contract.ts` and its siblings, with no path
 * overlap. Contract only: the handlers arrive in follow-up changes (CLAUDE.md invariant 8). The
 * extended `POST /leave/requests/:leaveRequestId/cancel` stays in `leave.contract.ts` next to its
 * route and takes its body from `../leave-changes.js`.
 *
 * THREE RECORDS, ONE SHAPE. Each is filed by the person it concerns (a cancellation may also be filed
 * by a manager or HR), decided through `@hrms/workflow`, and ends `approved`, `rejected` or
 * `withdrawn`. The approve and reject routes are `routeMeta(null)` because the approver is whoever
 * the workflow assigned, exactly as in `leave-encashment.contract.ts`; withdraw is the requester's,
 * checked against the row. None of these moves money, so none takes an `Idempotency-Key`: a
 * duplicate comp-off request is refused by the database (409), a second open cancellation or
 * amendment on one leave request likewise, and approve/reject/withdraw on a decided row is a 409.
 *
 * Reads take a `scope` (`mine`, `team`, `all`); `all` needs `leave.balance:adjust`. Nothing here is
 * hidden from the copilot: reads run as the caller and every write is a proposal the person confirms.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  amendLeaveRequestBodySchema,
  compOffRequestSchema,
  createCompOffRequestBodySchema,
  decideLeaveChangeBodySchema,
  leaveAmendmentSchema,
  leaveCancellationRequestSchema,
  listCompOffRequestsQuerySchema,
  listLeaveAmendmentsQuerySchema,
  listLeaveCancellationRequestsQuerySchema,
  withdrawLeaveChangeBodySchema,
} from '../leave-changes.js';
import { errorBodySchema, paginatedSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idParams = z.object({ id: uuidSchema });
const leaveRequestParams = z.object({ leaveRequestId: uuidSchema });

/** The shared error responses of a transition (withdraw, approve, reject) on a decided row. */
const transitionErrors = {
  401: errorBodySchema,
  403: errorBodySchema,
  404: errorBodySchema,
  /** No longer pending. */
  409: errorBodySchema,
} as const;

export const leaveChangesContract = c.router(
  {
    // ---- Comp-off requests --------------------------------------------------------------
    createCompOffRequest: {
      method: 'POST',
      path: '/comp-off-requests',
      body: createCompOffRequestBodySchema,
      responses: {
        201: compOffRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Someone already asked for this day (pending or approved), or a credit for it exists. */
        409: errorBodySchema,
        /** Not a past day, not a holiday or weekly off for the person, or no comp-off leave type. */
        422: errorBodySchema,
      },
      summary: 'Ask to be credited comp-off for a holiday or weekly off you worked, and start the approval',
      metadata: routeMeta('leave.comp_off:request'),
    },

    listCompOffRequests: {
      method: 'GET',
      path: '/comp-off-requests',
      query: listCompOffRequestsQuerySchema,
      responses: {
        200: paginatedSchema(compOffRequestSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Comp-off requests: your own, your team’s, or (with balance adjust) everyone’s',
      metadata: routeMeta(null),
    },

    withdrawCompOffRequest: {
      method: 'POST',
      path: '/comp-off-requests/:id/withdraw',
      pathParams: idParams,
      body: withdrawLeaveChangeBodySchema,
      responses: { 200: compOffRequestSchema, ...transitionErrors },
      summary: 'Pull a pending comp-off request back',
      metadata: routeMeta(null),
    },

    approveCompOffRequest: {
      method: 'POST',
      path: '/comp-off-requests/:id/approve',
      pathParams: idParams,
      body: decideLeaveChangeBodySchema,
      responses: { 200: compOffRequestSchema, ...transitionErrors },
      summary: 'Approve a comp-off request — creates the approved comp-off credit',
      metadata: routeMeta(null),
    },

    rejectCompOffRequest: {
      method: 'POST',
      path: '/comp-off-requests/:id/reject',
      pathParams: idParams,
      body: decideLeaveChangeBodySchema,
      responses: { 200: compOffRequestSchema, ...transitionErrors },
      summary: 'Reject a pending comp-off request',
      metadata: routeMeta(null),
    },

    // ---- Cancellation requests (the filing route is `cancelRequest` in leave.contract.ts) --
    listCancellationRequests: {
      method: 'GET',
      path: '/cancellation-requests',
      query: listLeaveCancellationRequestsQuerySchema,
      responses: {
        200: paginatedSchema(leaveCancellationRequestSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Cancellations of leave: your own, your team’s, or (with balance adjust) everyone’s',
      metadata: routeMeta(null),
    },

    withdrawCancellationRequest: {
      method: 'POST',
      path: '/cancellation-requests/:id/withdraw',
      pathParams: idParams,
      body: withdrawLeaveChangeBodySchema,
      responses: { 200: leaveCancellationRequestSchema, ...transitionErrors },
      summary: 'Pull a pending cancellation back — the leave stays as it was',
      metadata: routeMeta(null),
    },

    approveCancellationRequest: {
      method: 'POST',
      path: '/cancellation-requests/:id/approve',
      pathParams: idParams,
      body: decideLeaveChangeBodySchema,
      responses: { 200: leaveCancellationRequestSchema, ...transitionErrors },
      summary: 'Approve a cancellation — the days return to the balance and attendance is recalculated',
      metadata: routeMeta(null),
    },

    rejectCancellationRequest: {
      method: 'POST',
      path: '/cancellation-requests/:id/reject',
      pathParams: idParams,
      body: decideLeaveChangeBodySchema,
      responses: { 200: leaveCancellationRequestSchema, ...transitionErrors },
      summary: 'Reject a cancellation — the leave stays as it was',
      metadata: routeMeta(null),
    },

    // ---- Amendments ---------------------------------------------------------------------
    amendRequest: {
      method: 'POST',
      path: '/requests/:leaveRequestId/amend',
      pathParams: leaveRequestParams,
      body: amendLeaveRequestBodySchema,
      responses: {
        201: leaveAmendmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Another amendment or a cancellation is already open on this request. */
        409: errorBodySchema,
        /** Not approved, an hourly request, new dates the leave rules refuse, or not enough balance. */
        422: errorBodySchema,
      },
      summary: 'Ask to move your approved leave to other dates — it stays as approved until this is approved',
      metadata: routeMeta('leave:request'),
    },

    listAmendments: {
      method: 'GET',
      path: '/amendments',
      query: listLeaveAmendmentsQuerySchema,
      responses: {
        200: paginatedSchema(leaveAmendmentSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Leave amendments: your own, your team’s, or (with balance adjust) everyone’s',
      metadata: routeMeta(null),
    },

    withdrawAmendment: {
      method: 'POST',
      path: '/amendments/:id/withdraw',
      pathParams: idParams,
      body: withdrawLeaveChangeBodySchema,
      responses: { 200: leaveAmendmentSchema, ...transitionErrors },
      summary: 'Pull a pending amendment back — the leave stays as approved',
      metadata: routeMeta(null),
    },

    approveAmendment: {
      method: 'POST',
      path: '/amendments/:id/approve',
      pathParams: idParams,
      body: decideLeaveChangeBodySchema,
      responses: {
        200: leaveAmendmentSchema,
        ...transitionErrors,
        /** Applying found the request no longer has the dates it had when the amendment was filed
         * (409 above), or the balance no longer covers the new dates (here). */
        422: errorBodySchema,
      },
      summary: 'Approve an amendment — the leave moves to the new dates and the balance is adjusted by the difference',
      metadata: routeMeta(null),
    },

    rejectAmendment: {
      method: 'POST',
      path: '/amendments/:id/reject',
      pathParams: idParams,
      body: decideLeaveChangeBodySchema,
      responses: { 200: leaveAmendmentSchema, ...transitionErrors },
      summary: 'Reject an amendment — the leave stays as approved',
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/leave' },
);
